// What the leagues and match pages show and no endpoint answers. The fixture itself is real now: only these
// fields are invented — apps/frontend/docs/campionati-e-partite.md § «Le due aree su dati finti».

import type { PlayerPosition } from '../../messages/index.ts'

/** How far the pitch recording got. Each value is one of the wireframe's states. */
export const RECORDING_STATES = ['ready', 'processing', 'unavailable', 'tracking_failed'] as const

export type RecordingState = (typeof RECORDING_STATES)[number]

export interface MockHighlight {
  readonly id: number
  readonly minute: number
  readonly action: string
  readonly playerName: string
  readonly playerId: number
  readonly durationSeconds: number
  readonly origin: string
  /** Whose action it was: the band of its page is that side's club's. */
  readonly side: 'home' | 'away'
  readonly videoAvailable: boolean
}

export interface MockScoreLine {
  readonly minute: number
  readonly action: string
  readonly playerName: string
  readonly side: 'home' | 'away'
}

export interface MockLineupPlayer {
  readonly id: number
  readonly name: string
  readonly position: PlayerPosition
  readonly shirtNumber: number | null
  readonly starting: boolean
  readonly calledUp: boolean
  readonly hasAccount: boolean
}

export interface MatchExtras {
  readonly matchId: number
  readonly official: boolean
  /** Both sides are enrolments of ours, so this is not a datum in waiting: it is a
   *  distinction the model does not have. Marked, not removed — the slice's design doc. */
  readonly bothOnVolee: boolean
  readonly durationMinutes: number
  readonly recording: RecordingState
  /** Whether the viewer already has this recording. Invented like the rest: no endpoint
   *  answers it, and no price list exists either. */
  readonly unlocked: boolean
  readonly highlights: readonly MockHighlight[]
  readonly scoreLines: readonly MockScoreLine[]
  readonly lineup: readonly MockLineupPlayer[]
}

const LINEUP_71: readonly MockLineupPlayer[] = [
  {
    id: 901,
    name: 'Andrea Costa',
    position: 'goalkeeper',
    shirtNumber: 1,
    starting: true,
    calledUp: true,
    hasAccount: true,
  },
  {
    id: 902,
    name: 'Matteo Rebora',
    position: 'defender',
    shirtNumber: 4,
    starting: true,
    calledUp: true,
    hasAccount: true,
  },
  {
    id: 903,
    name: 'Samuele Pisano',
    position: 'defender',
    shirtNumber: 5,
    starting: false,
    calledUp: true,
    hasAccount: true,
  },
  {
    id: 904,
    name: 'Davide Oliveri',
    position: 'midfielder',
    shirtNumber: null,
    starting: false,
    calledUp: false,
    hasAccount: false,
  },
  {
    id: 905,
    name: 'Nicolò Traverso',
    position: 'midfielder',
    shirtNumber: 10,
    starting: true,
    calledUp: true,
    hasAccount: true,
  },
  {
    id: 906,
    name: 'Filippo Gandolfo',
    position: 'forward',
    shirtNumber: 9,
    starting: true,
    calledUp: true,
    hasAccount: true,
  },
]

export const MOCK_EXTRAS: readonly MatchExtras[] = [
  {
    matchId: 71,
    unlocked: true,
    official: true,
    bothOnVolee: true,
    durationMinutes: 94,
    recording: 'ready',
    highlights: [
      {
        id: 801,
        minute: 12,
        action: 'goal',
        playerName: 'Filippo Gandolfo',
        playerId: 906,
        durationSeconds: 24,
        origin: 'corner',
        side: 'home',
        videoAvailable: true,
      },
      {
        id: 802,
        minute: 38,
        action: 'save',
        playerName: 'Andrea Costa',
        playerId: 901,
        durationSeconds: 16,
        origin: 'counter',
        side: 'home',
        videoAvailable: true,
      },
      {
        id: 803,
        minute: 67,
        action: 'goal',
        playerName: 'Nicolò Traverso',
        playerId: 905,
        durationSeconds: 31,
        origin: 'open_play',
        side: 'home',
        videoAvailable: false,
      },
    ],
    scoreLines: [
      { minute: 12, action: 'goal', playerName: 'Filippo Gandolfo', side: 'home' },
      { minute: 55, action: 'goal', playerName: 'Marco Delfino', side: 'away' },
      { minute: 67, action: 'goal', playerName: 'Nicolò Traverso', side: 'home' },
    ],
    lineup: LINEUP_71,
  },
  {
    matchId: 72,
    unlocked: false,
    official: false,
    bothOnVolee: false,
    durationMinutes: 0,
    recording: 'unavailable',
    highlights: [],
    scoreLines: [],
    lineup: LINEUP_71,
  },
  {
    matchId: 73,
    unlocked: false,
    official: false,
    bothOnVolee: false,
    durationMinutes: 0,
    recording: 'processing',
    highlights: [],
    scoreLines: [],
    lineup: LINEUP_71,
  },
  {
    matchId: 75,
    unlocked: false,
    official: true,
    bothOnVolee: true,
    durationMinutes: 90,
    recording: 'ready',
    highlights: [
      {
        id: 851,
        minute: 18,
        action: 'goal',
        playerName: 'Filippo Gandolfo',
        playerId: 906,
        durationSeconds: 27,
        origin: 'open_play',
        side: 'home',
        videoAvailable: true,
      },
    ],
    scoreLines: [],
    lineup: LINEUP_71,
  },
  {
    matchId: 74,
    unlocked: false,
    official: true,
    bothOnVolee: true,
    durationMinutes: 91,
    recording: 'tracking_failed',
    highlights: [],
    scoreLines: [{ minute: 22, action: 'goal', playerName: 'Alessio Bruzzone', side: 'home' }],
    lineup: [],
  },
]
